import { cn } from "@/lib/utils"

import { HouseRule, ManualSection, Plate } from "./primitives"

interface SurfaceTileProps {
  role: string
  name: string
  use: string
  className: string
  tone?: "ink" | "on-brand"
}

function SurfaceTile({
  role,
  name,
  use,
  className,
  tone = "ink",
}: SurfaceTileProps) {
  const onBrand = tone === "on-brand"
  return (
    <div
      className={cn("flex min-h-36 flex-col rounded-xl border p-5", className)}
    >
      <span
        className={cn(
          "text-xs font-semibold tracking-wide uppercase",
          onBrand ? "text-tertiary_on-brand" : "text-tertiary",
        )}
      >
        {role}
      </span>
      <span
        className={cn(
          "mt-1.5 font-semibold",
          onBrand ? "text-primary_on-brand" : "text-primary",
        )}
      >
        {name}
      </span>
      <span
        className={cn(
          "mt-auto pt-3 text-sm",
          onBrand ? "text-secondary_on-brand" : "text-secondary",
        )}
      >
        {use}
      </span>
    </div>
  )
}

// The warm-neutral taxonomy. Oat is the single warm ground for both product
// and editorial surfaces; Paper White lifts cards, Manila groups them,
// and navy adds weight. Cream is no longer a flat surface — it lives only in
// the .app-canvas atmospheric wash.
export function SurfacesSection() {
  return (
    <ManualSection
      id="surfaces"
      title="Surfaces: the warm-neutral ground"
      lede="Oat is the single warm ground — it carries both dense product pages and the editorial/public canvas, because pure white reads clinical. Paper White lifts cards, Manila groups them, and navy adds weight. Cream itself is no longer a flat surface; it survives only as the .app-canvas atmospheric wash. The warm neutrals share one clay-trace ground (R>G>B), so the family reads as drawn from a single earth. Layer depth tonally rather than washing the page in one color."
    >
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SurfaceTile
          role="Canvas & base"
          name="Oat"
          use="The one warm ground: dense product pages and editorial/public canvas alike."
          className="bg-surface-base border-secondary"
        />
        <SurfaceTile
          role="Card"
          name="Paper White"
          use="Elevated content; lifts off the base with shadow-xs."
          className="bg-surface-elevated border-transparent shadow-xs"
        />
        <SurfaceTile
          role="Grouping"
          name="Manila"
          use="The warm second layer for panels and card groupings."
          className="bg-surface-grouped border-transparent"
        />
        <SurfaceTile
          role="Weight"
          name="Masthead Navy"
          use="Structure: masthead, footers, strong sections."
          className="bg-surface-strong border-transparent"
          tone="on-brand"
        />
      </div>

      <h3 className="text-primary text-xl font-semibold">
        Tonal layering on a product surface
      </h3>
      <div className="grid gap-6 lg:grid-cols-5">
        <Plate
          title="White cards on a Manila panel"
          note="Manila groups related material; Paper White cards lift off it with shadow-xs. Separation stays tonal. It needs no cream field and no UUI gray-50 (this is the warm surface-grouped handle)."
          className="lg:col-span-3"
        >
          <div className="bg-surface-grouped flex flex-col gap-3 rounded-lg p-4">
            <p className="text-tertiary text-xs font-semibold tracking-wide uppercase">
              Pinned to the desk
            </p>
            <div className="bg-primary rounded-lg p-4 shadow-xs">
              <p className="text-primary text-sm font-semibold">
                Breathe Me — Sia
              </p>
              <p className="text-tertiary text-sm">
                Suggested by Mara Quinn · May 9
              </p>
            </div>
            <div className="bg-primary rounded-lg p-4 shadow-xs">
              <p className="text-primary text-sm font-semibold">
                Holocene — Bon Iver
              </p>
              <p className="text-tertiary text-sm">
                Suggested by June Okafor · May 21
              </p>
            </div>
          </div>
        </Plate>

        <Plate
          title="Navy as weight"
          note="Strong sections like the masthead, footers, and brand blocks use navy as deliberate mass, never as a card treatment."
          className="lg:col-span-2"
          bodyClassName="p-0"
        >
          <div className="bg-brand-section flex h-full min-h-44 flex-col justify-end gap-1 p-6">
            <p className="text-primary_on-brand text-md font-semibold">
              Built by the herd
            </p>
            <p className="text-tertiary_on-brand text-sm">
              23 suggestions · 6 contributors
            </p>
          </div>
        </Plate>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Plate
          title="Shadow vocabulary"
          note="shadow-xs is the default and near-only resting shadow. The larger UUI steps are reserved for elements that genuinely float."
        >
          <div className="flex flex-wrap items-center gap-6">
            <div className="border-secondary bg-primary flex h-24 w-40 items-center justify-center rounded-lg border shadow-xs">
              <span className="text-tertiary text-sm">shadow-xs · resting</span>
            </div>
            <div className="bg-primary flex h-24 w-40 items-center justify-center rounded-lg shadow-lg">
              <span className="text-tertiary text-sm">
                shadow-lg · floating
              </span>
            </div>
          </div>
        </Plate>
        <HouseRule name="Cream is a canvas, not a coat">
          Cream is no longer a flat fill at all — a de-yellowed cream landed on
          top of Oat, so the canvas now <em>is</em> Oat. Default the page to the
          Oat ground, keep Paper White for cards, group with Manila, and let
          cream live only where it always belonged: the one .app-canvas
          atmospheric wash on public pages. Layer depth tonally: white on oat,
          navy as weight.
        </HouseRule>
      </div>
    </ManualSection>
  )
}
